import { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { BsFillInboxesFill, BsX } from "react-icons/bs";
import { FiSend } from "react-icons/fi";
import { AMBER_COLORS } from "./config";
import { joinSupplierPartRows } from "./utils/joinSupplierPartRows";
import { PartQuantity, SupplierPart } from "./utils/partsCalculationUtils";
import {
  useCreatePartConfirmationRequest,
  useGetPartConfirmationResponses,
} from "../../views/Orders/queryHooks";
import useNotification from "../../utils/useNotification";

const getModalStyles = (isFromTopSuppliers: boolean) => ({
  headerBox: {
    bgGradient: isFromTopSuppliers
      ? `linear(to-r, white, ${AMBER_COLORS[200]})`
      : "linear(to-r, #1976f2, #5b2be0)",
    px: 6,
    py: 4,
    position: "relative" as const,
    borderTopRadius: "xl",
    borderBottom: isFromTopSuppliers ? "1px solid" : "none",
    borderColor: isFromTopSuppliers ? AMBER_COLORS[100] : "transparent",
  },
  modalBody: {
    px: 6,
    py: 4,
    flex: 1,
    overflowY: "auto" as const,
    bg: "#fbfbfb",
    fontFamily: "'Nunito','Inter', sans-serif",
    sx: {
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  tableBox: {
    border: "1px solid",
    borderColor: "gray.200",
    borderRadius: "lg",
    boxShadow: "xs",
    p: 4,
    bg: "white",
  },
  modalFooter: {
    bg: "#fffdf9",
    borderTop: "1px solid",
    borderColor: "gray.200",
    justifyContent: "flex-end",
    gap: 3,
    position: "sticky" as const,
    bottom: 0,
    zIndex: 1,
  },
});

const RequestPartConfirmationModal = ({
  supplierLabel,
  isOpen,
  onClose,
  preFacetedPartNumbers,
  finalHitMap,
  isFromTopSuppliers = false,
  orderId,
  orderNo,
}: {
  supplierLabel: string;
  isOpen: boolean;
  onClose: () => void;
  preFacetedPartNumbers: PartQuantity[];
  finalHitMap: Map<string, Array<SupplierPart & { id?: string }>>;
  isFromTopSuppliers?: boolean;
  orderId: string;
  orderNo: string;
}) => {
  const notify = useNotification();
  const createRequest = useCreatePartConfirmationRequest();
  const supplierId = supplierLabel.split("::")[1] ?? "";
  const supplierName = supplierLabel.split("::")[0] ?? supplierLabel;
  const finalSupplierPartsMap = useMemo(
    () => finalHitMap.get(supplierLabel) ?? [],
    [finalHitMap, supplierLabel],
  );
  const rows = useMemo(
    () => joinSupplierPartRows(preFacetedPartNumbers, finalSupplierPartsMap),
    [preFacetedPartNumbers, finalSupplierPartsMap],
  );
  const { data: confirmationData } = useGetPartConfirmationResponses(
    orderNo,
    supplierId,
    isOpen,
  );
  const confirmedByPartId = useMemo(() => {
    const map = new Map<
      string,
      { quantity: number; status?: "confirmed" | "declined" }
    >();
    (confirmationData?.parts ?? []).forEach(
      (part: {
        partId?: string;
        partNumber?: string;
        quantity?: number;
        status?: "confirmed" | "declined";
      }) => {
        const value = {
          quantity: part.quantity ?? 0,
          status: part.status,
        };
        if (part.partId) map.set(part.partId, value);
        if (part.partNumber) map.set(part.partNumber, value);
      },
    );
    return map;
  }, [confirmationData]);
  const defaultChecked = useMemo(
    () =>
      new Set(
        rows
          .filter((row) => row.askedQty === row.stockQty)
          .map((row) => row.partId),
      ),
    [rows],
  );
  const [selectedPartIds, setSelectedPartIds] = useState<Set<string>>(
    defaultChecked,
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedPartIds(
        new Set(
          rows
            .filter((row) => row.askedQty === row.stockQty)
            .map((row) => row.partId),
        ),
      );
    }
  }, [isOpen, rows]);
  const modalStyles = getModalStyles(isFromTopSuppliers);

  const allPartIds = useMemo(() => rows.map((row) => row.partId), [rows]);
  const allSelected =
    allPartIds.length > 0 && allPartIds.every((id) => selectedPartIds.has(id));
  const someSelected = allPartIds.some((id) => selectedPartIds.has(id));

  const toggleAll = () => {
    setSelectedPartIds((prev) => {
      const everySelected =
        allPartIds.length > 0 && allPartIds.every((id) => prev.has(id));
      return everySelected ? new Set() : new Set(allPartIds);
    });
  };

  const togglePart = (partId: string) => {
    setSelectedPartIds((prev) => {
      const next = new Set(prev);
      if (next.has(partId)) {
        next.delete(partId);
      } else {
        next.add(partId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const selectedRows = rows.filter((row) => selectedPartIds.has(row.partId));
    if (!orderId || !orderNo || !supplierId || selectedRows.length === 0) {
      notify({
        status: "error",
        title: "Select at least one part",
        description: "Choose parts in this supplier's stock to confirm.",
      });
      return;
    }

    try {
      await createRequest.mutateAsync({
        orderId,
        orderNo,
        supplierId,
        parts: selectedRows.map((row) => ({
          partId: row.partId,
          partNumber: row.partNumber,
          rootPartNumber: row.rootPartNumber,
          partName: row.partName,
          askedQty: row.askedQty,
          stockQty: row.stockQty,
          binLocation: row.binLocation,
          parts: row.parts,
          alternateParts: row.alternateParts,
        })),
      });
      notify({
        status: "success",
        title: "Part confirmation requested",
        description: `Sent to ${supplierName}`,
      });
      onClose();
    } catch (error: any) {
      notify({
        status: "error",
        title: "Failed to request part confirmation",
        description: error?.response?.data?.message || error?.message,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="xl" overflow="hidden">
        <Box {...modalStyles.headerBox}>
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={isFromTopSuppliers ? "gray.800" : "white"}
          >
            Request part confirmation
          </Text>
          <Text
            fontSize="sm"
            color={isFromTopSuppliers ? "gray.600" : "whiteAlpha.800"}
          >
            {supplierName}
          </Text>
          <ModalCloseButton
            color={isFromTopSuppliers ? "gray.700" : "white"}
          />
        </Box>
        <ModalBody {...modalStyles.modalBody}>
          <Box {...modalStyles.tableBox}>
            <Text
              fontSize="md"
              fontWeight="semibold"
              mb={3}
              display="flex"
              alignItems="center"
              gap={2}
              color="gray.700"
            >
              <Icon
                as={BsFillInboxesFill}
                color={isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500"}
                boxSize={4}
              />
              Parts in stock
            </Text>
            <Box overflowX="auto" fontFamily="'Nunito','Inter', sans-serif">
              <Table size="md" variant="unstyled">
                <Thead bg="gray.100">
                  <Tr>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      <Checkbox
                        isChecked={allSelected}
                        isIndeterminate={someSelected && !allSelected}
                        onChange={toggleAll}
                        colorScheme={isFromTopSuppliers ? "orange" : "blue"}
                        aria-label="Select all parts"
                      />
                    </Th>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      Part #
                    </Th>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      Part name
                    </Th>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      Asked qty
                    </Th>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      Stock qty
                    </Th>
                    <Th textAlign="center" fontSize="md" color="gray.600">
                      Confirmed Qty
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row, index) => {
                    const confirmation =
                      confirmedByPartId.get(row.partId) ||
                      confirmedByPartId.get(row.partNumber);
                    const isDeclined = confirmation?.status === "declined";
                    const isConfirmed = confirmation?.status === "confirmed";

                    return (
                      <Tr
                        key={row.partId}
                        bg={index % 2 !== 0 ? "gray.50" : "white"}
                        fontSize="sm"
                      >
                        <Td textAlign="center">
                          <Checkbox
                            isChecked={selectedPartIds.has(row.partId)}
                            onChange={() => togglePart(row.partId)}
                            colorScheme={isFromTopSuppliers ? "orange" : "blue"}
                          />
                        </Td>
                        <Td
                          textAlign="center"
                          fontWeight="bold"
                          color="gray.700"
                        >
                          {row.partNumber}
                        </Td>
                        <Td textAlign="center" color="gray.700">
                          {row.partName || "—"}
                        </Td>
                        <Td textAlign="center" color="gray.700">
                          {row.askedQty}
                        </Td>
                        <Td textAlign="center" color="gray.700">
                          {row.stockQty}
                        </Td>
                        <Td
                          textAlign="center"
                          fontSize="xl"
                          color={
                            isDeclined
                              ? "red.500"
                              : isConfirmed && (confirmation?.quantity ?? 0) > 0
                                ? "green.500"
                                : "gray.700"
                          }
                          fontWeight={
                            isDeclined ||
                            (isConfirmed && (confirmation?.quantity ?? 0) > 0)
                              ? "semibold"
                              : "normal"
                          }
                        >
                          {isDeclined
                            ? 0
                            : isConfirmed && (confirmation?.quantity ?? 0) > 0
                              ? confirmation.quantity
                              : "-"}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter {...modalStyles.modalFooter}>
          <Button
            variant="ghost"
            color={isFromTopSuppliers ? AMBER_COLORS[700] : "gray.700"}
            leftIcon={<Icon as={BsX} />}
            onClick={onClose}
            _hover={{
              bg: isFromTopSuppliers ? AMBER_COLORS[50] : "gray.100",
            }}
          >
            Close
          </Button>
          <Button
            leftIcon={<Icon as={FiSend} />}
            color="white"
            bg={isFromTopSuppliers ? AMBER_COLORS[600] : "blue.500"}
            _hover={{
              bg: isFromTopSuppliers ? AMBER_COLORS[700] : "blue.600",
            }}
            isLoading={createRequest.isLoading}
            isDisabled={selectedPartIds.size === 0}
            onClick={handleSubmit}
          >
            Request part confirmation
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RequestPartConfirmationModal;
